/**
 * Unified Image Editor API Client
 *
 * Orchestriert 3 Bildservices (Conversion, AspectRatio, Upscale)
 * mit Settings-Hierarchie (Channel > Integration > System Default).
 */

import { apiClient } from '../api-client';
import type { AxiosError } from 'axios';

// =====================================================
// Types
// =====================================================

export interface ProcessImageParams {
  asset_id: string;

  // Optional: Explizite Settings (überschreiben Defaults)
  target_format?: 'png' | 'jpeg' | 'webp' | 'avif';
  quality?: number; // 1-100
  target_width?: number;
  target_height?: number;
  aspect_ratio?: string; // "16:9", "1:1", etc
  upscale_factor?: number; // 1.0-2.0
  upscale_model?: 'fsrcnn' | 'espcn' | 'lapsrn' | 'edsr';

  // Optional: Settings Source
  integration_id?: string;
  sales_channel_id?: string;

  // Execution
  save_as_new?: boolean; // default: true
  execution_mode?: 'auto' | 'sync' | 'async'; // default: auto
}

export interface ProcessBatchParams {
  asset_ids: string[];
  // Same settings as ProcessImageParams
  target_format?: string;
  quality?: number;
  target_width?: number;
  target_height?: number;
  aspect_ratio?: string;
  upscale_factor?: number;
  upscale_model?: string;
  integration_id?: string;
  sales_channel_id?: string;
  save_as_new?: boolean;
}

export interface ProcessingResult {
  success: boolean;
  mode: 'sync' | 'async';
  status: 'completed' | 'pending' | 'failed';
  result_asset_id?: string;
  task_id?: string;
  error?: string;
  processing_time_ms?: number;
  idempotency_key?: string;
}

export interface ProcessingStatus {
  status: 'pending' | 'completed' | 'failed';
  progress?: number; // 0-100
  result?: {
    result_asset_id: string;
    processing_time_ms: number;
  };
  error?: string;
}

// =====================================================
// API Client Functions
// =====================================================

/**
 * Verarbeitet einzelnes Bild durch Unified Image Editor Pipeline.
 *
 * @param params - Processing Parameters
 * @returns ProcessingResult (sync: result_asset_id, async: task_id)
 */
export async function processImage(
  params: ProcessImageParams
): Promise<ProcessingResult> {
  const response = await apiClient.post<ProcessingResult>(
    '/api/v2/unified-image-editor/process',
    params
  );
  return response.data;
}

/**
 * Verarbeitet hochgeladenes Bild durch Unified Image Editor Pipeline.
 *
 * @param file - Image file to upload and process
 * @param settings - Processing settings (format, dimensions, upscale)
 * @returns ProcessingResult (sync: result_asset_id, async: task_id)
 */
export async function processImageUpload(
  file: File,
  settings: Omit<ProcessImageParams, 'asset_id'>
): Promise<ProcessingResult> {
  const formData = new FormData();
  formData.append('file', file);

  // Add all settings to FormData
  if (settings.target_format) {
    formData.append('target_format', settings.target_format);
  }
  if (settings.quality !== undefined) {
    formData.append('quality', String(settings.quality));
  }
  if (settings.target_width !== undefined) {
    formData.append('target_width', String(settings.target_width));
  }
  if (settings.target_height !== undefined) {
    formData.append('target_height', String(settings.target_height));
  }
  if (settings.aspect_ratio) {
    formData.append('aspect_ratio', settings.aspect_ratio);
  }
  if (settings.upscale_factor !== undefined) {
    formData.append('upscale_factor', String(settings.upscale_factor));
  }
  if (settings.upscale_model) {
    formData.append('upscale_model', settings.upscale_model);
  }
  if (settings.integration_id) {
    formData.append('integration_id', settings.integration_id);
  }
  if (settings.sales_channel_id) {
    formData.append('sales_channel_id', settings.sales_channel_id);
  }
  if (settings.save_as_new !== undefined) {
    formData.append('save_as_new', String(settings.save_as_new));
  }
  if (settings.execution_mode) {
    formData.append('execution_mode', settings.execution_mode);
  }

  const response = await apiClient.post<ProcessingResult>(
    '/api/v2/unified-image-editor/process-upload',
    formData
  );
  return response.data;
}

/**
 * Verarbeitet mehrere Bilder (Batch).
 * IMMER async (>10 Bilder).
 *
 * @param params - Batch Processing Parameters
 * @returns ProcessingResult mit task_id
 */
export async function processBatch(
  params: ProcessBatchParams
): Promise<ProcessingResult> {
  const response = await apiClient.post<ProcessingResult>(
    '/api/v2/unified-image-editor/batch',
    params
  );
  return response.data;
}

/**
 * Fetched Status für Async Processing Job.
 *
 * @param taskId - Task ID vom Async Result
 * @returns ProcessingStatus
 */
export async function fetchProcessingStatus(
  taskId: string
): Promise<ProcessingStatus> {
  const response = await apiClient.get<ProcessingStatus>(
    `/api/v2/unified-image-editor/status/${taskId}`
  );
  return response.data;
}

// =====================================================
// Settings API (Integration/Channel Defaults)
// =====================================================

export interface IntegrationDefaults {
  id: string;
  workspace_id: string;
  integration_id: string;
  default_format?: string;
  default_quality?: number;
  default_width?: number;
  default_height?: number;
  default_aspect_ratio?: string;
  default_upscale_factor?: number;
  default_upscale_model?: string;
}

export interface ChannelOverrides {
  id: string;
  workspace_id: string;
  sales_channel_id: string;
  override_format?: string;
  override_quality?: number;
  override_width?: number;
  override_height?: number;
  override_aspect_ratio?: string;
  override_upscale_factor?: number;
  override_upscale_model?: string;
}

/**
 * Lädt Integration Defaults.
 */
export async function getIntegrationDefaults(
  integrationId: string
): Promise<IntegrationDefaults | null> {
  try {
    const response = await apiClient.get<IntegrationDefaults>(
      `/api/v2/image-settings/integration/${integrationId}/defaults`
    );
    return response.data;
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) return null;
    throw error;
  }
}

/**
 * Speichert Integration Defaults.
 */
export async function updateIntegrationDefaults(
  integrationId: string,
  defaults: Partial<IntegrationDefaults>
): Promise<IntegrationDefaults> {
  const response = await apiClient.put<IntegrationDefaults>(
    `/api/v2/image-settings/integration/${integrationId}/defaults`,
    defaults
  );
  return response.data;
}

/**
 * Lädt Channel Overrides.
 */
export async function getChannelOverrides(
  channelId: string
): Promise<ChannelOverrides | null> {
  try {
    const response = await apiClient.get<ChannelOverrides>(
      `/api/v2/image-settings/channel/${channelId}/overrides`
    );
    return response.data;
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) return null;
    throw error;
  }
}

/**
 * Speichert Channel Overrides.
 */
export async function updateChannelOverrides(
  channelId: string,
  overrides: Partial<ChannelOverrides>
): Promise<ChannelOverrides> {
  const response = await apiClient.put<ChannelOverrides>(
    `/api/v2/image-settings/channel/${channelId}/overrides`,
    overrides
  );
  return response.data;
}
